import { Component, OnInit, AfterViewInit, TemplateRef, ViewChild, Input, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { fadeInOut } from '../../../services/animations';
import { AlertService, DialogType, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from "../../../services/app-translation.service";
import { Utilities } from "../../../services/utilities";
import { ListViewViewModel } from '../../model/listViewViewModel';
import { Configuration, BusinessOverviewViewModel, BusinessService } from '../../index';
import { Router, ActivatedRoute, NavigationExtras } from '@angular/router';
import { Observable } from 'rxjs';
import { Permission } from '../../../models/permission.model';
import { AuthService } from '../../../services/auth.service';

@Component({
    selector: 'app-business-list',
    templateUrl: './business-list.component.html',
    styleUrls: ['./business-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BusinessListComponent implements OnInit, AfterViewInit {
    @ViewChild(MatPaginator, { static: true })
    paginator: MatPaginator;
    @ViewChild(MatSort, { static: true })
    sort: MatSort;
    @Input() organizationId: number = null;

    displayedColumns = ['name', 'enabled', 'businessCategoryName', 'regionName', 'updatedDate', 'actions'];
    dataSource: MatTableDataSource<BusinessOverviewViewModel>;
    loadingIndicator: boolean;

    // Filtering
    @Input() currentViewId: string = "all";
    @Input() showTitle: boolean = true;
    @Input() fixedView: boolean = false;
    currentSearchText: string;

    availableViews: Observable<ListViewViewModel[]>;


    constructor(private alertService: AlertService,
        private translationService: AppTranslationService,
        private authService: AuthService,
        private businessService: BusinessService,
        private snackBar: MatSnackBar,
        private dialog: MatDialog,
        private router: Router,
        private route: ActivatedRoute) {

        this.dataSource = new MatTableDataSource<BusinessOverviewViewModel>();
        this.currentSearchText = "";

    }

  ngOnInit() {
      this.availableViews = this.businessService.getBusinessViews();

      this.availableViews.subscribe(views => {
          if (this.route.snapshot.fragment && views.find(t => t.viewId === this.route.snapshot.fragment)) {
              this.currentViewId = this.route.snapshot.fragment;
          }

          this.loadData();
      });
  }

    ngAfterViewInit(): void {
        if (!this.organizationId) {
            // Sorting only available in full list mode
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
        }
    }

    public applyFilter(filterValue: string) {
        this.dataSource.filter = filterValue;
    }

    private refresh() {
        // Causes the filter to refresh there by updating with recently added data.
        this.applyFilter(this.dataSource.filter);
    }

    public loadData() {
        this.alertService.startLoadingMessage();
        this.loadingIndicator = true;

        this.businessService.getBusinesses(this.currentViewId, this.organizationId).subscribe(
            bus => this.onDataLoadSuccessful(bus),
            error => this.onDataLoadFailed(error)
        );
    }

    private onDataLoadSuccessful(businesses: BusinessOverviewViewModel[]) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;
        this.dataSource.data = businesses;
    }

    private onDataLoadFailed(error: any) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.alertService.showStickyMessage(this.translationService.getTranslation("Error"), this.translationService.getTranslation("FetchErrorMessage") + "\r\n" + Utilities.getHttpResponseMessage(error),
            MessageSeverity.error, error);
    }


    confirmDelete(business: BusinessOverviewViewModel) {
        let message: string = this.translationService.getTranslation("commands.Delete") +
            " " +
            business.name +
            "?";
        let action: string = this.translationService.getTranslation("commands.Delete").toUpperCase();

        this.snackBar.open(message, action, { duration: 5000 })
            .onAction().subscribe(() => {
                this.alertService.startLoadingMessage(
                    this.translationService.getTranslation("globalTexts.DeletingMessage"));
                this.loadingIndicator = true;

                this.businessService.deleteBusiness(business.businessId)
                    .subscribe(results => {
                        this.alertService.stopLoadingMessage();
                        this.loadingIndicator = false;
                        this.alertService.showMessage(this.translationService.getTranslation("commands.Delete"),
                            business.name + " " + this.translationService.getTranslation("globalTexts.deleted"),
                            MessageSeverity.success);
                        this.loadData();
                    },
                    error => {
                        this.alertService.stopLoadingMessage();
                        this.loadingIndicator = false;

                        let errorMessage = this.translationService.getTranslation("globalTexts.Error") +
                            ": " +
                            Utilities.getHttpResponseMessage(error);

                        this.alertService.showStickyMessage(
                            this.translationService.getTranslation("globalTexts.DeleteError"),
                            errorMessage,
                            MessageSeverity.error,
                            error);
                    });
            });
    }

    public viewChanged(selectedOption: MatSelect) {
        var fragment: NavigationExtras =
            {
                fragment: selectedOption.value
            };
        this.router.navigate(['/businesses'], fragment);
        this.loadData();
    }

    gotoBusinessDetails(): void {
        if (this.organizationId) {
            this.router.navigate(['/businesses/business'], { queryParams: { organizationid: this.organizationId} });
        } else {
            this.router.navigate(['/businesses/business']);
        }
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.changeBusinesses);
    }

    hasPermissionToDelete(): boolean {
        return this.authService.userHasPermission(Permission.deleteOrganizations);
    }

}
